import MyCapacityRequestsCard from './scheduling/capacity/MyCapacityRequestsCard';
import ConversationsHeaderToggle from '../components/communications/ConversationsHeaderToggle';
import ConversationsCard from '../components/communications/ConversationsCard';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { ArrowRightOnRectangleIcon, MapPinIcon, UsersIcon, ShieldCheckIcon, SparklesIcon, CheckCircleIcon, UserIcon, CodeBracketIcon, XMarkIcon, PlusIcon, MinusIcon, MapIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { GridStack } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import PermissionsContainer from '../components/PermissionsContainer';
import { usePermissionsMode } from '../contexts/PermissionsModeContext';
import { usePermissions } from '../contexts/PermissionContext';
import DashboardPanelIcon from '../components/icons/DashboardPanelIcon';
import PlannerIcon from '../components/icons/PlannerIcon';
import userSettingsService from '../services/userSettings.service';
import aiService, { CompletedImprovement } from '../services/ai.service';
import api from '../services/api';
import { groupReleasesByDate, type FeatureRelease } from '../utils/featureReleases';
import styles from '../styles/Home.module.css';

interface CardConfig {
  key: string;
  title: string;
  column: number;
  order: number;
  visible: boolean;
  span: number;
  workspaceId: string;
  locked?: boolean; // Per-card lock: when locked the card can't be moved/resized (default true)
  x: number;
  y: number;
  w: number;
  h: number;
  gsX?: number;
  gsY?: number;
  gsW?: number;
  gsH?: number;
  fontSize?: number; // Font size scale: 0.75, 0.875, 1, 1.125, 1.25
}

function Home() {
  const navigate = useNavigate();
  const [showResolvedConversations, setShowResolvedConversations] = useState(false);
  const { instance, accounts } = useMsal();
  const currentUser = accounts[0];
  const { setPageComponents } = usePermissionsMode();
  const { hasAccess, isInGroups } = usePermissions();

  // State for hidden cards dropdown
  const [showHiddenCardsMenu, setShowHiddenCardsMenu] = useState(false);

  // GridStack refs
  const gridRef = useRef<HTMLDivElement>(null);
  const gridInstanceRef = useRef<GridStack | null>(null);
  const isGridInitializing = useRef<boolean>(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveStateRef = useRef<{ cardConfigs: CardConfig[] }>({ cardConfigs: [] });

  // State for AI improvements
  const [aiImprovements, setAiImprovements] = useState<CompletedImprovement[]>([]);
  const [aiImprovementsLoading, setAiImprovementsLoading] = useState(true);

  // State for git activity summaries
  interface GitSummary {
    date: string;
    dayName: string;
    formatted: string;
    summary: string;
    commitCount: number;
  }
  const [gitSummaries, setGitSummaries] = useState<GitSummary[]>([]);
  const [gitActivityLoading, setGitActivityLoading] = useState(true);

  // State for feature releases (Closed 'feature'-tagged stories + AI synopses)
  const [featureReleases, setFeatureReleases] = useState<FeatureRelease[]>([]);
  const [featureReleasesLoading, setFeatureReleasesLoading] = useState(true);

  // State for Azure DevOps work items (grouped by Sprint)
  interface WorkItem {
    id: number;
    parentId: number | null;
    title: string;
    type: 'User Story' | 'Task' | 'Bug';
    state: string;
    priority: number | null;
    assignedTo: string | null;
    url: string;
    closedDate: string | null;
    isClosed: boolean;
    children: WorkItem[];
  }
  interface SprintGroup {
    sprint: string;
    items: WorkItem[];
    counts: {
      total: number;
      closed: number;
      userStories: number;
      tasks: number;
      bugs: number;
    };
  }
  const [sprints, setSprints] = useState<SprintGroup[]>([]);
  const [workItemsLoading, setWorkItemsLoading] = useState(true);
  // Work item type icons (SVG)
  const WorkItemIcon = ({ type, size = 14, color }: { type: string; size?: number; color: string }) => {
    if (type === 'User Story') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill={color}>
          <path d="m14.773,2.716C18.91-.625,24.006.115,24.006.115c0,0-.696,5.418-4.833,8.759-1.495,1.207-3.036,1.879-4.4,2.251.612-2.125,3.089-5.89,5.228-8.241-3.62,2.004-6.464,5.629-7.588,8.647-.322.821-.555,1.649-.756,2.469h-2.061c.237-1.041.536-2.13.97-3.227.194-1.587.986-5.454,4.208-8.057Zm5.227,13.284h4v4c0,2.206-1.794,4-4,4h-11c-2.206,0-4-1.794-4-4v-12H0v-4C0,1.794,1.794,0,4,0h11.214c-.573.331-1.143.711-1.698,1.16h0c-.708.572-1.305,1.196-1.827,1.84h-3.831c.084.322.142.653.142,1v16c0,.551.448,1,1,1s1-.449,1-1v-4h7v-3.54c.981-.408,1.995-.97,3-1.72v5.26ZM5,4c0-.551-.448-1-1-1s-1,.449-1,1v1h2v-1Zm8,15v1c0,.345-.044.68-.127,1h7.127c.552,0,1-.449,1-1v-1h-8Z"/>
        </svg>
      );
    }
    if (type === 'Task') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill={color}>
          <path d="m19,0H5C2.243,0,0,2.243,0,5v14c0,2.757,2.243,5,5,5h11.646c1.287,0,2.497-.501,3.407-1.411l2.536-2.536c.897-.896,1.411-2.139,1.411-3.407V5c0-2.757-2.243-5-5-5ZM2,19V5c0-1.654,1.346-3,3-3h14c1.654,0,3,1.346,3,3v10h-4c-1.654,0-3,1.346-3,3v4H5c-1.654,0-3-1.346-3-3Zm16.639,2.175c-.448.448-1.02.725-1.639.802v-3.977c0-.552.449-1,1-1h3.976c-.079.615-.361,1.198-.802,1.639l-2.536,2.536ZM5,6.5c0-.828.672-1.5,1.5-1.5s1.5.672,1.5,1.5-.672,1.5-1.5,1.5-1.5-.672-1.5-1.5Zm3,5.5c0,.828-.672,1.5-1.5,1.5s-1.5-.672-1.5-1.5.672-1.5,1.5-1.5,1.5.672,1.5,1.5Zm0,5.5c0,.828-.672,1.5-1.5,1.5s-1.5-.672-1.5-1.5.672-1.5,1.5-1.5,1.5.672,1.5,1.5Z"/>
        </svg>
      );
    }
    // Bug
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size} fill={color}>
        <path d="M0,19v4a1,1,0,0,0,2,0V19H4.93a8.248,8.248,0,0,0,14.14,0H22v4a1,1,0,0,0,2,0V19a2,2,0,0,0-2-2H19.947a8.2,8.2,0,0,0,.325-2.273A8.37,8.37,0,0,0,20.2,13.7l-.175-.7H23a1,1,0,0,0,0-2H19.522L18.513,7H22a2,2,0,0,0,2-2V1a1,1,0,0,0-2,0V5H18c-.018,0-.032.009-.05.01a6.411,6.411,0,0,0-11.89,0C6.039,5.011,6.021,5,6,5H2V1A1,1,0,0,0,0,1V5A2,2,0,0,0,2,7H5.485L4.472,11H1a1,1,0,0,0,0,2H3.965l-.145.573L3.8,13.7a8.37,8.37,0,0,0-.07,1.032A8.2,8.2,0,0,0,4.053,17H2A2,2,0,0,0,0,19Z"/>
      </svg>
    );
  };

  // AbortController refs for canceling API requests
  const aiAbortRef = useRef<AbortController | null>(null);
  const gitAbortRef = useRef<AbortController | null>(null);
  const featureReleasesAbortRef = useRef<AbortController | null>(null);
  const workItemsAbortRef = useRef<AbortController | null>(null);

  // Fetch AI improvements on mount
  useEffect(() => {
    const controller = new AbortController();
    aiAbortRef.current = controller;

    const fetchAiImprovements = async () => {
      try {
        const userEmail = currentUser?.username;
        const response = await aiService.getRecentCompletedSuggestions(5, userEmail, controller.signal);
        setAiImprovements(response.improvements);
      } catch (error: any) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error('Error fetching AI improvements:', error);
      } finally {
        setAiImprovementsLoading(false);
      }
    };

    fetchAiImprovements();
    return () => aiAbortRef.current?.abort();
  }, [currentUser]);

  // Fetch git activity summaries on mount
  useEffect(() => {
    const controller = new AbortController();
    gitAbortRef.current = controller;

    const fetchGitSummaries = async () => {
      try {
        const response = await api.get('/git-activity/summaries?days=90', { signal: controller.signal });
        setGitSummaries(response.data.summaries || []);
      } catch (error: any) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error('Error fetching git summaries:', error);
      } finally {
        setGitActivityLoading(false);
      }
    };

    fetchGitSummaries();
    return () => gitAbortRef.current?.abort();
  }, []);

  // Fetch feature releases on mount
  useEffect(() => {
    const controller = new AbortController();
    featureReleasesAbortRef.current = controller;

    const fetchFeatureReleases = async () => {
      try {
        const response = await api.get('/feature-releases/list?days=90', { signal: controller.signal });
        setFeatureReleases(response.data.releases || []);
      } catch (error: any) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error('Error fetching feature releases:', error);
      } finally {
        setFeatureReleasesLoading(false);
      }
    };

    fetchFeatureReleases();
    return () => featureReleasesAbortRef.current?.abort();
  }, []);

  // Fetch Azure DevOps work items (grouped by Sprint) on mount
  useEffect(() => {
    const controller = new AbortController();
    workItemsAbortRef.current = controller;

    const fetchWorkItems = async () => {
      try {
        const response = await api.get('/git-activity/work-items', { signal: controller.signal });
        setSprints(response.data.sprints || []);
      } catch (error: any) {
        if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;
        console.error('Error fetching work items:', error);
      } finally {
        setWorkItemsLoading(false);
      }
    };

    fetchWorkItems();
    return () => workItemsAbortRef.current?.abort();
  }, []);

  // Define all page components
  const pageComponentsList = useMemo(() => [
    { id: 'home-user-info-card', name: 'User Information Card' },
    { id: 'home-quick-links-card', name: 'Quick Links Card' },
    { id: 'home-stats-card', name: 'Statistics Card' },
    { id: 'home-ai-improvements-card', name: 'Latest AI Implementations Card' },
    { id: 'home-dev-activity-card', name: 'Dev Activity Card' },
    { id: 'home-feature-releases-card', name: 'Latest Feature Releases Card' },
    { id: 'home-tickets-card', name: 'Tickets Card' },
    { id: 'home-conversations-card', name: 'Your Conversations Card' },
    { id: 'home-capacity-requests-card', name: 'My Capacity Requests Card' },
  ], []);

  // Get default card configurations
  const getDefaultCardConfigs = (): CardConfig[] => [
    { key: 'capacity-requests-card', title: 'My Capacity Requests', column: 0, order: 5, visible: true, span: 12, workspaceId: 'home-default', locked: true, x: 0, y: 25, w: 12, h: 5 },
    { key: 'conversations-card', title: 'Your Conversations', column: 0, order: 4, visible: true, span: 12, workspaceId: 'home-default', locked: true, x: 0, y: 20, w: 12, h: 5 },
    {
      key: 'dev-activity-card',
      title: 'Dev Activity',
      column: 0,
      order: 0,
      visible: true,
      span: 12,
      workspaceId: 'home-default',
      locked: true,
      x: 0,
      y: 0,
      w: 12,
      h: 5,
    },
    {
      key: 'feature-releases-card',
      title: 'Latest Feature Releases',
      column: 0,
      order: 1,
      visible: true,
      span: 12,
      workspaceId: 'home-default',
      locked: true,
      x: 0,
      y: 5,
      w: 12,
      h: 5,
    },
    {
      key: 'tickets-card',
      title: 'Tickets',
      column: 0,
      order: 2,
      visible: true,
      span: 12,
      workspaceId: 'home-default',
      locked: true,
      x: 0,
      y: 10,
      w: 12,
      h: 5,
    },
    {
      key: 'quick-links-card',
      title: 'Quick Links',
      column: 0,
      order: 3,
      visible: true,
      span: 12,
      workspaceId: 'home-default',
      locked: true,
      x: 0,
      y: 15,
      w: 12,
      h: 5,
    },
  ];

  // Card configuration state - start with defaults, will be replaced by loaded layout
  const [cardConfigs, setCardConfigs] = useState<CardConfig[]>(getDefaultCardConfigs());
  const [layoutLoaded, setLayoutLoaded] = useState(false); // Wait for layout to load before rendering grid

  // GridStack item interface for positioning
  interface GridItem {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
  }

  // Valid card keys - filter out stale/unknown cards from saved layouts
  // 'gwi360-movers-card' was removed 2026-07-30 — leaving it out here also drops the stale
  // saved rows from both the grid and the hidden-cards menu.
  // 'gwi360-utilization-card' and 'dispatcher-metrics-card' were removed 2026-09-16 the same
  // way; Dispatcher Metrics lives on at /researching-reports/dispatcher-metrics. Migration
  // 459 deletes the orphaned saved rows.
  const validCardKeys = new Set(['user-info-card', 'quick-links-card', 'stats-card', 'ai-improvements-card', 'dev-activity-card', 'feature-releases-card', 'tickets-card', 'conversations-card', 'capacity-requests-card']);

  // GWI360-IT group check for tickets card
  const isITGroup = isInGroups(['016b50f3-9ff1-4170-bd15-6b70734f4fc7']);

  // Get grid items - uses stored GridStack positions if available
  const currentGridItems = useMemo((): GridItem[] => {
    return cardConfigs
      .filter(card => card.visible !== false) // Only show visible cards
      .filter(card => validCardKeys.has(card.key)) // Only show known cards
      .filter(card => card.key !== 'tickets-card' || isITGroup) // Tickets only for GWI360-IT
      .map(card => {
        // If GridStack positions are stored AND valid, use them directly
        if (card.gsX !== undefined && card.gsY !== undefined && card.gsW !== undefined && card.gsH !== undefined
            && card.gsW >= 1 && card.gsH >= 1) {
          return { id: card.key, x: card.gsX, y: card.gsY, w: card.gsW, h: card.gsH };
        }
        // Otherwise use default positions
        return { id: card.key, x: card.x, y: card.y, w: card.w, h: card.h };
      });
  }, [cardConfigs, isITGroup]);

  // Load saved layout on mount (cards already render with defaults, this updates positions if saved)
  useEffect(() => {
    const loadLayout = async () => {
      try {
        const layout = await userSettingsService.getDashboardLayoutNew('home');
        // Only use saved layout if it has actual cards
        if (layout && layout.cardConfigs && layout.cardConfigs.length > 0) {
          // Map backend card configs to ensure all fields are present
          const mappedCards = layout.cardConfigs.map((card: any) => ({
            key: card.key,
            title: card.title,
            column: card.column || 0,
            order: card.order || 0,
            visible: card.visible ?? true,
            span: card.span || 4,
            workspaceId: card.workspaceId || 'home-default',
            locked: card.locked ?? true,
            x: card.x || card.gsX || 0,
            y: card.y || card.gsY || 0,
            w: card.w || card.gsW || 4,
            h: card.h || card.gsH || 4,
            gsX: card.gsX,
            gsY: card.gsY,
            gsW: card.gsW,
            gsH: card.gsH,
            fontSize: card.fontSize || 1,
          }));

          // Merge any new default cards that aren't in the saved layout
          const defaults = getDefaultCardConfigs();
          const savedKeys = new Set(mappedCards.map((c: CardConfig) => c.key));
          const newCards = defaults.filter(d => !savedKeys.has(d.key));

          if (newCards.length > 0) {
            mappedCards.push(...newCards);
          }

          setCardConfigs(mappedCards);
        }
      } catch (error) {
        console.error('Failed to load home layout:', error);
      } finally {
        // Mark layout as loaded so GridStack can initialize
        setLayoutLoaded(true);
      }
    };

    loadLayout();
  }, []);

  // Register components with permissions mode toolbar
  useEffect(() => {
    setPageComponents(pageComponentsList);

    // Cleanup on unmount
    return () => {
      setPageComponents([]);
    };
  }, [pageComponentsList, setPageComponents]);

  // Save layout function
  const saveLayout = useCallback((updatedCardConfigs: CardConfig[], immediate = false) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Update ref immediately so we always have latest state
    saveStateRef.current = { cardConfigs: updatedCardConfigs };

    const doSave = async () => {
      try {
        await userSettingsService.saveDashboardLayoutNew('home', {
          cardConfigs: updatedCardConfigs,
          columnCount: 12,
          workspaces: [
            {
              id: 'home-default',
              name: 'Home',
              order: 0,
              columnCount: 12
            }
          ],
          collapsed: {},
          currentWorkspaceId: 'home-default',
          version: '1',
        });
      } catch (error) {
        console.error('Failed to save home layout:', error);
      }
    };

    if (immediate) {
      // Save immediately without debounce
      doSave();
    } else {
      // Debounce save by 1 second
      saveTimeoutRef.current = setTimeout(doSave, 1000);
    }
  }, []);

  // Get list of hidden cards
  const hiddenCards = useMemo(() => {
    return cardConfigs.filter(c => c.visible === false && validCardKeys.has(c.key));
  }, [cardConfigs]);

  // Hide a card
  const hideCard = useCallback((cardKey: string) => {
    const grid = gridInstanceRef.current;

    // Remove from GridStack first
    if (grid) {
      const element = document.querySelector(`[gs-id="${cardKey}"]`) as HTMLElement;
      if (element) {
        grid.removeWidget(element, false);
      }
    }

    // Update state only - the autosave effect persists the change
    setCardConfigs(prev => prev.map(c =>
      c.key === cardKey ? { ...c, visible: false } : c
    ));
  }, []);

  // Show a hidden card
  const showCard = useCallback((cardKey: string) => {
    // Update state only - the autosave effect persists the change
    setCardConfigs(prev => prev.map(c =>
      c.key === cardKey ? { ...c, visible: true } : c
    ));
    setShowHiddenCardsMenu(false);
  }, []);

  // Font size scale options (50% to 300%)
  const fontSizeScales = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.5, 3];

  // Increase font size for a card
  const increaseFontSize = useCallback((cardKey: string) => {
    // Update state only - the autosave effect persists the change
    setCardConfigs(prev => prev.map(c => {
      if (c.key !== cardKey) return c;
      const currentSize = c.fontSize || 1;
      const currentIndex = fontSizeScales.findIndex(s => Math.abs(s - currentSize) < 0.01);
      const newIndex = currentIndex === -1 ? 3 : Math.min(currentIndex + 1, fontSizeScales.length - 1);
      return { ...c, fontSize: fontSizeScales[newIndex] };
    }));
  }, []);

  // Decrease font size for a card
  const decreaseFontSize = useCallback((cardKey: string) => {
    // Update state only - the autosave effect persists the change
    setCardConfigs(prev => prev.map(c => {
      if (c.key !== cardKey) return c;
      const currentSize = c.fontSize || 1;
      const currentIndex = fontSizeScales.findIndex(s => Math.abs(s - currentSize) < 0.01);
      const newIndex = currentIndex === -1 ? 1 : Math.max(currentIndex - 1, 0);
      return { ...c, fontSize: fontSizeScales[newIndex] };
    }));
  }, []);

  // Toggle a single card's lock state (locked = can't move/resize).
  // Applies the change to the GridStack widget immediately; state change is autosaved.
  const toggleCardLock = useCallback((cardKey: string) => {
    const current = saveStateRef.current.cardConfigs;
    const willUnlock = (current.find(c => c.key === cardKey)?.locked ?? true);
    const updated = current.map(c =>
      c.key === cardKey ? { ...c, locked: !(c.locked ?? true) } : c
    );

    // Tell GridStack to enable/disable move + resize for just this widget
    const grid = gridInstanceRef.current;
    if (grid) {
      const el = document.querySelector(`[gs-id="${cardKey}"]`) as HTMLElement | null;
      if (el && (el as any).gridstackNode) {
        grid.movable(el, willUnlock);
        grid.resizable(el, willUnlock);
      }
    }

    setCardConfigs(updated);
  }, []);

  // Close the hidden-cards menu once there are no hidden cards left
  useEffect(() => {
    if (hiddenCards.length === 0) {
      setShowHiddenCardsMenu(false);
    }
  }, [hiddenCards.length]);

  // Keep saveStateRef in sync with cardConfigs
  useEffect(() => {
    saveStateRef.current = { cardConfigs };
  }, [cardConfigs]);

  // Autosave layout whenever cardConfigs change (move/resize, lock, hide, font size).
  // Skip the very first run so we don't re-save the freshly-loaded layout.
  const didSkipInitialSave = useRef(false);
  useEffect(() => {
    if (!layoutLoaded) return;
    if (!didSkipInitialSave.current) {
      didSkipInitialSave.current = true;
      return;
    }
    saveLayout(cardConfigs);
  }, [cardConfigs, layoutLoaded, saveLayout]);

  // Initialize GridStack
  useEffect(() => {
    if (!gridRef.current || !layoutLoaded) return;

    isGridInitializing.current = true;

    const timer = setTimeout(() => {
      if (!gridRef.current) return;

      // Drag/resize are enabled at the grid level; each widget is then locked or
      // unlocked individually via grid.movable/resizable based on its `locked` flag.
      const grid = GridStack.init({
        column: 12,
        cellHeight: 80,
        margin: 4,
        float: false,
        animate: true,
        resizable: {
          handles: 'se',
        },
      }, gridRef.current);

      gridInstanceRef.current = grid;

      // After grid is initialized, make all cards into widgets and apply lock state
      setTimeout(() => {
        currentGridItems.forEach(item => {
          const element = document.querySelector(`[gs-id="${item.id}"]`) as HTMLElement;
          if (!element) return;
          if (!element.classList.contains('ui-draggable')) {
            try {
              grid.makeWidget(element);
            } catch (e) {
              console.error('Error making widget:', item.id, e);
            }
          }
          // Lock by default — only unlocked cards can be moved/resized
          const unlocked = !(cardConfigs.find(c => c.key === item.id)?.locked ?? true);
          grid.movable(element, unlocked);
          grid.resizable(element, unlocked);
        });
      }, 50);

      // Listen for changes and update cardConfigs with new positions
      grid.on('change', () => {
        // Skip change events during initialization
        if (isGridInitializing.current) {
          return;
        }

        const nodes = grid.getGridItems();

        // Build a map of card positions from GridStack
        const positionMap: { [id: string]: { x: number; y: number; w: number; h: number } } = {};
        nodes.forEach((el) => {
          const node = el.gridstackNode;
          const domId = el.getAttribute('gs-id');
          const domX = parseInt(el.getAttribute('gs-x') || '0', 10);
          const domY = parseInt(el.getAttribute('gs-y') || '0', 10);
          const domW = parseInt(el.getAttribute('gs-w') || '4', 10);
          const domH = parseInt(el.getAttribute('gs-h') || '4', 10);

          const id = node?.id || domId;
          const x = node?.x ?? domX;
          const y = node?.y ?? domY;
          const w = node?.w ?? domW;
          const h = node?.h ?? domH;

          if (id) {
            positionMap[id] = { x, y, w, h };
          }
        });

        // Update cardConfigs state with new positions
        setCardConfigs((prevConfigs) => {
          const updated = prevConfigs.map((card) => {
            const pos = positionMap[card.key];
            if (pos) {
              return {
                ...card,
                gsX: pos.x,
                gsY: pos.y,
                gsW: pos.w,
                gsH: pos.h,
                x: pos.x,
                y: pos.y,
                w: pos.w,
                h: pos.h,
                span: pos.w, // Update span to match width
              };
            }
            return card;
          });

          // Only update state here; the autosave effect persists the change (debounced).
          return updated;
        });
      });

      // After a short delay, allow change events (initialization is complete)
      setTimeout(() => {
        isGridInitializing.current = false;
      }, 200);
    }, 100);

    return () => {
      clearTimeout(timer);
      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (gridInstanceRef.current) {
        gridInstanceRef.current.destroy(false);
        gridInstanceRef.current = null;
      }
    };
  }, [layoutLoaded, saveLayout]);

  // Track card keys to detect when new cards are added
  const prevCardKeysRef = useRef<Set<string>>(new Set());

  // Handle new cards being added - use GridStack's makeWidget API
  useEffect(() => {
    if (!gridInstanceRef.current || !layoutLoaded) return;

    const currentKeys = new Set(currentGridItems.map(item => item.id));
    const prevKeys = prevCardKeysRef.current;

    // Find newly added cards
    const newKeys = [...currentKeys].filter(key => !prevKeys.has(key));

    if (newKeys.length > 0) {
      // Give React time to render the new DOM elements, then make them widgets
      setTimeout(() => {
        if (!gridInstanceRef.current) return;

        newKeys.forEach(key => {
          const grid = gridInstanceRef.current;
          if (!grid) return;
          const element = document.querySelector(`[gs-id="${key}"]`) as HTMLElement;
          if (!element) return;
          if (!element.classList.contains('ui-draggable')) {
            grid.makeWidget(element);
          }
          // Apply this card's lock state to the new widget
          const unlocked = !(saveStateRef.current.cardConfigs.find(c => c.key === key)?.locked ?? true);
          grid.movable(element, unlocked);
          grid.resizable(element, unlocked);
        });
      }, 50);
    }

    prevCardKeysRef.current = currentKeys;
  }, [currentGridItems, layoutLoaded]);

  const handleLogout = () => {
    instance.logoutRedirect({
      postLogoutRedirectUri: '/',
    });
  };

  // Render card content based on card key
  const renderCardContent = (card: CardConfig, fontSize: number = 1) => {
    // Use zoom to scale all content (works with fixed pixel sizes)
    const zoomStyle = fontSize !== 1 ? { zoom: fontSize } : {};

    switch (card.key) {
      case 'capacity-requests-card':
        return <MyCapacityRequestsCard />;
      case 'conversations-card':
        return <ConversationsCard showResolved={showResolvedConversations} />;
      case 'user-info-card':
        return (
          <div className={styles.cardContent} style={zoomStyle}>
            {currentUser ? (
              <div className={styles.userInfoDetails}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Name:</span>
                  <span className={styles.infoValue}>{currentUser.name}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Email:</span>
                  <span className={styles.infoValue}>{currentUser.username}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Tenant ID:</span>
                  <span className={styles.infoValue}>{currentUser.tenantId}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className={styles.signOutButton}
                >
                  <ArrowRightOnRectangleIcon className={styles.signOutIcon} />
                  Sign Out
                </button>
              </div>
            ) : (
              <p className={styles.noUserInfo}>No user information available</p>
            )}
          </div>
        );

      case 'quick-links-card':
        // Define all possible quick links
        const allQuickLinks = [
          {
            route: '/dispatching/dispatch-dashboard',
            icon: <DashboardPanelIcon className={styles.quickLinkIcon} />,
            label: 'Dispatch Dashboard'
          },
          {
            route: '/dispatching/dispatch-planner',
            icon: <PlannerIcon className={styles.quickLinkIcon} />,
            label: 'Dispatch Planner'
          },
          {
            route: '/fleet-management/live-map',
            icon: <MapPinIcon className={styles.quickLinkIcon} />,
            label: 'Live Map'
          },
          {
            route: '/fleet-management/drivers',
            icon: <UsersIcon className={styles.quickLinkIcon} />,
            label: 'Drivers'
          },
          {
            route: '/administration/permissions',
            icon: <ShieldCheckIcon className={styles.quickLinkIcon} />,
            label: 'Permissions'
          },
          {
            route: '/administration/users',
            icon: <UsersIcon className={styles.quickLinkIcon} />,
            label: 'Users'
          },
          {
            route: '/roadmap',
            icon: <MapIcon className={styles.quickLinkIcon} />,
            label: 'Roadmap'
          }
        ];

        // Filter links based on user permissions
        const availableQuickLinks = allQuickLinks.filter(link => hasAccess(link.route));

        return (
          <div className={styles.cardContent} style={zoomStyle}>
            <div className={styles.quickLinks}>
              {availableQuickLinks.map((link, index) => (
                <div
                  key={index}
                  onClick={() => navigate(link.route)}
                  className={styles.quickLink}
                >
                  {link.icon}
                  <span className={styles.quickLinkText}>{link.label}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'ai-improvements-card':
        return (
          <div className={styles.cardContent} style={zoomStyle}>
            {aiImprovementsLoading ? (
              <div className={styles.statsLoading}>Loading improvements...</div>
            ) : aiImprovements.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgb(var(--c-gray-500))', padding: '1rem' }}>
                No AI improvements completed yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', overflowY: 'auto', maxHeight: '100%' }}>
                {aiImprovements.map((improvement) => (
                  <div
                    key={improvement.id}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: 'rgb(var(--c-green-50))',
                      borderRadius: '0.375rem',
                      borderLeft: '3px solid #22c55e',
                      position: 'relative',
                    }}
                  >
                    {improvement.user_asked && (
                      <div style={{
                        position: 'absolute',
                        top: '0.375rem',
                        right: '0.375rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        backgroundColor: 'rgb(var(--c-blue-100))',
                        color: 'rgb(var(--c-blue-700))',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.25rem',
                        fontSize: '0.625rem',
                        fontWeight: 600,
                      }}>
                        <UserIcon style={{ width: '0.625rem', height: '0.625rem' }} />
                        Mine
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <CheckCircleIcon style={{ width: '1rem', height: '1rem', color: 'rgb(var(--c-green-500))', flexShrink: 0, marginTop: '0.125rem' }} />
                      <div style={{ flex: 1, minWidth: 0, paddingRight: improvement.user_asked ? '3rem' : 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'rgb(var(--c-gray-900))' }}>
                          {improvement.title}
                        </div>
                        {improvement.completion_notes && (
                          <div style={{ fontSize: '0.75rem', color: 'rgb(var(--c-emerald-800))', marginTop: '0.25rem', fontStyle: 'italic' }}>
                            Fix: {improvement.completion_notes}
                          </div>
                        )}
                      </div>
                    </div>
                    {improvement.questions_addressed && improvement.questions_addressed.length > 0 && (
                      <div style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                        <div style={{ fontSize: '0.625rem', color: 'rgb(var(--c-gray-500))', fontWeight: 500, marginBottom: '0.25rem' }}>
                          Questions Addressed:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '0.75rem', fontSize: '0.7rem', color: 'rgb(var(--c-gray-600))' }}>
                          {improvement.questions_addressed.slice(0, 2).map((q, i) => (
                            <li key={i} style={{ marginBottom: '0.125rem' }}>{q}</li>
                          ))}
                          {improvement.questions_addressed.length > 2 && (
                            <li style={{ color: 'rgb(var(--c-gray-400))' }}>+{improvement.questions_addressed.length - 2} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                    <div style={{ fontSize: '0.625rem', color: 'rgb(var(--c-gray-400))', marginTop: '0.25rem', paddingLeft: '1.5rem' }}>
                      {improvement.completed_at && new Date(improvement.completed_at).toLocaleDateString()}
                      {improvement.completed_by && ` by ${improvement.completed_by}`}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'dev-activity-card':
        return (
          <div className={styles.cardContent} style={{ flexDirection: 'column', ...zoomStyle }}>
            {gitActivityLoading ? (
                <div className={styles.statsLoading}>Loading summaries...</div>
              ) : gitSummaries.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgb(var(--c-gray-500))', padding: '1rem', width: '100%' }}>
                  No summaries yet. Run sync to generate.
                </div>
              ) : (
                <div className={styles.devActivityScroll}>
                  {gitSummaries.map((daySummary) => (
                    <div key={daySummary.date} style={{ marginBottom: '0.75rem' }}>
                      {/* Date Header */}
                      <div className={styles.devActivityDayHeader}>
                        {daySummary.dayName} ({daySummary.formatted})
                        <span className={styles.devActivityCommitCount}>
                          — {daySummary.commitCount} commit{daySummary.commitCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {/* AI Summary bullets */}
                      <div style={{ paddingLeft: '0.5rem' }}>
                        {daySummary.summary.split('\n').filter(line => line.trim()).map((line, idx) => {
                          const cleanLine = line.replace(/^[-•*]\s*/, '').trim();
                          if (!cleanLine) return null;
                          return (
                            <div
                              key={`${daySummary.date}-${idx}`}
                              className={styles.devActivityItem}
                            >
                              <span className={styles.devActivityBullet}>•</span>
                              <span>{cleanLine}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );

      case 'feature-releases-card':
        return (
          <div className={styles.cardContent} style={{ flexDirection: 'column', ...zoomStyle }}>
            {featureReleasesLoading ? (
                <div className={styles.statsLoading}>Loading feature releases...</div>
              ) : featureReleases.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'rgb(var(--c-gray-500))', padding: '1rem', width: '100%' }}>
                  No feature releases yet.
                </div>
              ) : (
                <div className={styles.devActivityScroll}>
                  {groupReleasesByDate(featureReleases).map((group) => (
                    <div key={group.date} style={{ marginBottom: '0.75rem' }}>
                      {/* Date Header */}
                      <div className={styles.devActivityDayHeader}>
                        {group.label}
                        <span className={styles.devActivityCommitCount}>
                          — {group.releases.length} release{group.releases.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {/* Feature synopses */}
                      <div style={{ paddingLeft: '0.5rem' }}>
                        {group.releases.map((release) => (
                          <div key={release.workItemId} className={styles.devActivityItem}>
                            <span className={styles.devActivityBullet}>•</span>
                            <span>
                              {release.url ? (
                                <a
                                  href={release.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ fontWeight: 600 }}
                                  className="hover:underline"
                                >
                                  {release.title}
                                </a>
                              ) : (
                                <span style={{ fontWeight: 600 }}>{release.title}</span>
                              )}
                              {' — '}
                              {release.synopsis}
                              <span style={{ display: 'block', fontSize: '0.625rem', color: 'rgb(var(--c-gray-400))', marginTop: '0.125rem' }}>
                                #{release.workItemId}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );

      case 'tickets-card':
        return (
          <div className={styles.cardContent} style={{ flexDirection: 'column', ...zoomStyle }}>
            {workItemsLoading ? (
              <div className={styles.statsLoading}>Loading tickets...</div>
            ) : sprints.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'rgb(var(--c-gray-500))', padding: '1rem', width: '100%' }}>
                No tickets yet. Run sync to fetch.
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, width: '100%' }}>
                {sprints.filter(s => s.sprint !== 'gwi360' && s.sprint !== 'Backlog').map((sprintGroup) => (
                  <div key={sprintGroup.sprint} style={{ marginBottom: '0.75rem' }}>
                    {/* Sprint Header */}
                    <div style={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: 'rgb(var(--c-gray-700))',
                      padding: '0.25rem 0.5rem',
                      backgroundColor: 'rgb(var(--c-gray-100))',
                      borderRadius: '0.25rem',
                      marginBottom: '0.375rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }}>
                      {sprintGroup.sprint === 'gwi360' ? 'Backlog' : sprintGroup.sprint}
                      <span style={{ fontSize: '0.6rem', fontWeight: 400, color: 'rgb(var(--c-gray-500))' }}>
                        {sprintGroup.counts.closed}/{sprintGroup.counts.total} done
                        </span>
                      </div>
                      {/* Work Items - Nested Structure */}
                      <div style={{ paddingLeft: '0.5rem' }}>
                        {sprintGroup.items.map((item) => (
                          <div key={item.id}>
                            {/* Parent Item (User Story or Bug) */}
                            <div
                              style={{
                                fontSize: '0.75rem',
                                color: item.isClosed ? '#9ca3af' : '#374151',
                                padding: '0.25rem 0',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.5rem',
                              }}
                            >
                              <span style={{
                                flexShrink: 0,
                                opacity: item.isClosed ? 0.5 : 1,
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                <WorkItemIcon
                                  type={item.type}
                                  size={14}
                                  color={item.type === 'User Story' ? '#3b82f6' : item.type === 'Task' ? '#f59e0b' : '#ef4444'}
                                />
                              </span>
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: item.isClosed ? '#9ca3af' : '#374151',
                                  textDecoration: item.isClosed ? 'line-through' : 'none',
                                }}
                                onMouseOver={(e) => { if (!item.isClosed) e.currentTarget.style.textDecoration = 'underline'; }}
                                onMouseOut={(e) => { e.currentTarget.style.textDecoration = item.isClosed ? 'line-through' : 'none'; }}
                              >
                                {item.title}
                              </a>
                              {item.isClosed && item.closedDate && (
                                <span style={{
                                  fontSize: '0.55rem',
                                  color: 'rgb(var(--c-gray-400))',
                                  fontWeight: 400,
                                }}>
                                  {item.closedDate}
                                </span>
                              )}
                            </div>
                            {/* Nested Child Tasks */}
                            {item.children && item.children.length > 0 && (
                              <div style={{ paddingLeft: '1.5rem', borderLeft: '2px solid #e5e7eb', marginLeft: '0.5rem' }}>
                                {item.children.map((child) => (
                                  <div
                                    key={child.id}
                                    style={{
                                      fontSize: '0.7rem',
                                      color: child.isClosed ? '#9ca3af' : '#6b7280',
                                      padding: '0.125rem 0',
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      gap: '0.5rem',
                                    }}
                                  >
                                    <span style={{
                                      flexShrink: 0,
                                      opacity: child.isClosed ? 0.5 : 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}>
                                      <WorkItemIcon
                                        type={child.type}
                                        size={12}
                                        color={child.type === 'Task' ? '#f59e0b' : '#ef4444'}
                                      />
                                    </span>
                                    <a
                                      href={child.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: child.isClosed ? '#9ca3af' : '#6b7280',
                                        textDecoration: child.isClosed ? 'line-through' : 'none',
                                      }}
                                      onMouseOver={(e) => { if (!child.isClosed) e.currentTarget.style.textDecoration = 'underline'; }}
                                      onMouseOut={(e) => { e.currentTarget.style.textDecoration = child.isClosed ? 'line-through' : 'none'; }}
                                    >
                                      {child.title}
                                    </a>
                                    {child.isClosed && child.closedDate && (
                                      <span style={{
                                        fontSize: '0.5rem',
                                        color: 'rgb(var(--c-gray-400))',
                                      }}>
                                        {child.closedDate}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        );

      default:
        return (
          <div className={styles.cardContent} style={zoomStyle}>
            <p>Card content coming soon...</p>
          </div>
        );
    }
  };

  return (
    <div className={styles.container}>
      {/* Loading State */}
      {!layoutLoaded && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <div className={styles.loadingText}>Loading Cards...</div>
        </div>
      )}

      {/* GridStack Layout */}
      <div
        ref={gridRef}
        className="grid-stack"
        key="home-grid"
        style={{ visibility: layoutLoaded ? 'visible' : 'hidden' }}
      >
        {layoutLoaded && currentGridItems.map((item) => {
          const cardConfig = cardConfigs.find(c => c.key === item.id);
          if (!cardConfig) return null;

          return (
            <div
              key={item.id}
              className="grid-stack-item"
              gs-id={item.id}
              gs-x={item.x}
              gs-y={item.y}
              gs-w={item.w}
              gs-h={item.h}
            >
              <div className="grid-stack-item-content">
                <PermissionsContainer
                  componentId={`home-${item.id}`}
                  componentName={cardConfig.title}
                >
                  <div className={styles.userInfoCard}>
                    <div className={styles.cardHeader}>
                      <h2 className={styles.cardTitle}>{cardConfig.title}</h2>
                      <div className="ml-auto flex items-center gap-1">
                        {cardConfig.key === 'conversations-card' && <ConversationsHeaderToggle checked={showResolvedConversations} onChange={setShowResolvedConversations} />}
                        {/* Font size + hide controls only show while the card is unlocked */}
                        {!(cardConfig.locked ?? true) && (
                          <>
                            {/* Font size controls */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                decreaseFontSize(cardConfig.key);
                              }}
                              className="p-1 rounded hover:bg-white/20 transition-colors"
                              title="Decrease font size"
                            >
                              <MinusIcon className="w-3.5 h-3.5 text-white" />
                            </button>
                            <span className="text-white text-xs font-medium w-8 text-center">
                              {Math.round((cardConfig.fontSize || 1) * 100)}%
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                increaseFontSize(cardConfig.key);
                              }}
                              className="p-1 rounded hover:bg-white/20 transition-colors"
                              title="Increase font size"
                            >
                              <PlusIcon className="w-3.5 h-3.5 text-white" />
                            </button>
                            {/* Divider */}
                            <div className="w-px h-4 bg-white/30 mx-1" />
                            {/* Hide button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                hideCard(cardConfig.key);
                              }}
                              className="p-1 rounded hover:bg-white/20 transition-colors"
                              title="Hide card"
                            >
                              <XMarkIcon className="w-4 h-4 text-white" />
                            </button>
                            {/* Divider */}
                            <div className="w-px h-4 bg-white/30 mx-1" />
                          </>
                        )}
                        {/* Lock / unlock toggle (always visible) */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCardLock(cardConfig.key);
                          }}
                          className="p-1 rounded hover:bg-white/20 transition-colors"
                          title={(cardConfig.locked ?? true) ? 'Unlock card to move and resize' : 'Lock card'}
                          aria-label={(cardConfig.locked ?? true) ? 'Unlock card' : 'Lock card'}
                        >
                          {(cardConfig.locked ?? true)
                            ? <LockClosedIcon className="w-4 h-4 text-white" />
                            : <LockOpenIcon className="w-4 h-4 text-white" />}
                        </button>
                      </div>
                    </div>
                    {renderCardContent(cardConfig, cardConfig.fontSize || 1)}
                  </div>
                </PermissionsContainer>
              </div>
            </div>
          );
        })}
      </div>

      {/* Floating button to restore hidden cards (replaces the removed edit-mode toolbar action) */}
      {layoutLoaded && hiddenCards.length > 0 && (
        <button
          onClick={() => setShowHiddenCardsMenu(prev => !prev)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-3 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium shadow-lg transition-colors"
          title="Show hidden cards"
          aria-label={`Show hidden cards (${hiddenCards.length})`}
        >
          <PlusIcon className="w-4 h-4" />
          Hidden ({hiddenCards.length})
        </button>
      )}

      {/* Hidden Cards Dropdown Menu */}
      {showHiddenCardsMenu && hiddenCards.length > 0 && (
        <div
          className="fixed bottom-20 right-6 bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50 min-w-64"
        >
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-heading text-sm">Hidden Cards</h3>
            <p className="text-xs text-muted">Click to restore a card</p>
          </div>
          <div className="p-2 max-h-64 overflow-y-auto">
            {hiddenCards.map((card) => (
              <button
                key={card.key}
                onClick={() => showCard(card.key)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                <PlusIcon className="w-4 h-4 text-green-600" />
                <span className="text-sm text-label">{card.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close hidden cards menu */}
      {showHiddenCardsMenu && (
        <div
          className="fixed inset-0 z-30"
          onClick={() => setShowHiddenCardsMenu(false)}
        />
      )}
    </div>
  );
}

export default Home;